import React from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  ScrollView,
  Platform,
} from 'react-native';
import {TemplateProps} from '../types';
import {Reminder} from '../../types';
import {styles} from './styles';

/**
 * Parse a reminder and get local date/time info
 */
interface ParsedReminder {
  reminder: Reminder;
  localDate: string;  // YYYY-MM-DD in local timezone
  localTime: string;  // HH:mm in local timezone, or '--:--' for all-day
}

function parseReminder(reminder: Reminder): ParsedReminder {
  if (reminder.isAllDay || !reminder.startTime) {
    // All-day event - use the date as-is
    return {
      reminder,
      localDate: reminder.date,
      localTime: '--:--',
    };
  }

  // Timed event - parse UTC and convert to local timezone
  const utcDate = new Date(reminder.startTime);
  const localDate = utcDate.toLocaleDateString('sv-SE'); // YYYY-MM-DD format
  const localTime = utcDate.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return {
    reminder,
    localDate,
    localTime,
  };
}

/**
 * Check if a date string (YYYY-MM-DD) is today
 */
function isToday(dateStr: string): boolean {
  const today = new Date();
  const todayStr = today.toLocaleDateString('sv-SE');
  return dateStr === todayStr;
}

/**
 * Check if a date string (YYYY-MM-DD) is tomorrow
 */
function isTomorrow(dateStr: string): boolean {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toLocaleDateString('sv-SE');
  return dateStr === tomorrowStr;
}

/**
 * Format a date label for grouping
 */
function formatDayLabel(dateStr: string, t: (key: string) => string): string {
  if (isToday(dateStr)) {
    return t('frame.today');
  }
  if (isTomorrow(dateStr)) {
    return t('frame.tomorrow');
  }
  const date = new Date(dateStr + 'T12:00:00'); // Use noon to avoid DST issues
  const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayOfWeek = date.getDay();

  // If within 7 days, show weekday name
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dateOnly = new Date(dateStr + 'T00:00:00');
  const diffDays = Math.floor((dateOnly.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays >= 0 && diffDays <= 6) {
    return t(`frame.weekdays.${weekdays[dayOfWeek]}`);
  }

  // Otherwise show short date
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Group reminders by local date
 */
interface ReminderGroup {
  date: string;
  label: string;
  isToday: boolean;
  reminders: ParsedReminder[];
}

function groupRemindersByDay(reminders: Reminder[], t: (key: string) => string): ReminderGroup[] {
  const groups: Map<string, ReminderGroup> = new Map();

  for (const reminder of reminders) {
    const parsed = parseReminder(reminder);
    if (!groups.has(parsed.localDate)) {
      groups.set(parsed.localDate, {
        date: parsed.localDate,
        label: formatDayLabel(parsed.localDate, t),
        isToday: isToday(parsed.localDate),
        reminders: [],
      });
    }
    groups.get(parsed.localDate)!.reminders.push(parsed);
  }

  // Sort groups by date and return as array
  return Array.from(groups.values()).sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Split Template Component
 *
 * A clean split layout:
 * - 2/3 photo, 1/3 info panel
 * - Landscape: photo left, info right
 * - Portrait: photo top, info bottom
 */
export default function SplitTemplate(props: TemplateProps) {
  const {
    currentPhotoUrl,
    previousPhotoUrl,
    isTransitioning,
    scaledImageSize,
    currentOffset,
    previousOffset,
    screenWidth,
    screenHeight,
    isPortrait,
    formattedTime,
    formattedDate,
    greeting,
    reminders,
    isEditMode,
    showSettings,
    showHint,
    isSaving,
    showSaved,
    loadingProgress,
    panOffset,
    onSettingsPress,
    onEditPress,
    onExitEditMode,
    onDismissHint,
    onDragStart,
    onDragMove,
    onDragEnd,
    panResponderHandlers,
    t,
    fontScale = 1.0,
    displayDeviceName,
  } = props;

  // Dynamic font sizes based on fontScale (date/time/greeting increased by 20%)
  const scaledFonts = {
    greeting: 22 * fontScale,      // was 18
    time: 58 * fontScale,          // was 48
    date: 19 * fontScale,          // was 16
    reminderTitle: 14 * fontScale,
    reminderTime: 12 * fontScale,
    remindersTitle: 14 * fontScale,
    dayLabel: 12 * fontScale,
    // Portrait sizes (also increased by 20%)
    portraitGreeting: 19 * fontScale,  // was 16
    portraitTime: 43 * fontScale,      // was 36
    portraitDate: 17 * fontScale,      // was 14
  };

  // Group reminders by day
  const reminderGroups = groupRemindersByDay(reminders.slice(0, 8), t);

  // Calculate photo section dimensions
  const photoSectionWidth = isPortrait ? screenWidth : (screenWidth * 2) / 3;
  const photoSectionHeight = isPortrait ? (screenHeight * 2) / 3 : screenHeight;

  // Render the photo section
  const renderPhotoSection = () => (
    <View style={isPortrait ? styles.portraitPhotoSection : styles.landscapePhotoSection}>
      <View style={styles.photoContainer}>
        {/* Previous photo (for crossfade) */}
        {isTransitioning && previousPhotoUrl && (
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
            }}>
            <Image
              source={{uri: previousPhotoUrl}}
              style={[
                styles.photo,
                {
                  width: scaledImageSize.width,
                  height: scaledImageSize.height,
                  marginLeft: -(scaledImageSize.width - photoSectionWidth) / 2 + previousOffset.x,
                  marginTop: -(scaledImageSize.height - photoSectionHeight) / 2 + previousOffset.y,
                },
              ]}
              resizeMode="cover"
            />
          </View>
        )}
        {/* Current photo */}
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            animation: isTransitioning ? 'fadeIn 0.8s ease-in-out' : 'none',
          } as any}>
          <Image
            source={{uri: currentPhotoUrl}}
            style={[
              styles.photo,
              {
                width: scaledImageSize.width,
                height: scaledImageSize.height,
                marginLeft: -(scaledImageSize.width - photoSectionWidth) / 2 + (isEditMode ? panOffset.x : currentOffset.x),
                marginTop: -(scaledImageSize.height - photoSectionHeight) / 2 + (isEditMode ? panOffset.y : currentOffset.y),
              },
            ]}
            resizeMode="cover"
          />
        </View>
      </View>

      {/* Edit mode overlay on photo section */}
      {isEditMode && (
        <>
          <View
            style={{
              position: 'absolute',
              top: 50,
              left: 0,
              right: 0,
              bottom: 70,
              zIndex: 80,
              cursor: 'grab',
              touchAction: 'none',
              userSelect: 'none',
            } as any}
            {...(Platform.OS === 'web' ? {
              onMouseDown: onDragStart,
              onMouseMove: onDragMove,
              onMouseUp: onDragEnd,
              onMouseLeave: onDragEnd,
              onTouchStart: onDragStart,
              onTouchMove: onDragMove,
              onTouchEnd: onDragEnd,
            } : panResponderHandlers)}
          />
          <View style={styles.editModeBorder} pointerEvents="none" />
          <View style={styles.editModeHeader} pointerEvents="box-none">
            <Text style={styles.editModeText}>{t('frame.dragToReposition')}</Text>
            {isSaving && <Text style={styles.editModeSaving}>{t('common.saving')}</Text>}
            {showSaved && <Text style={styles.editModeSaved}>{t('common.saved')}</Text>}
          </View>
          <View style={styles.editModeFooter}>
            <TouchableOpacity
              style={styles.editModeDoneButton}
              onPress={onExitEditMode}
              activeOpacity={0.8}>
              <Text style={styles.editModeDoneIcon}>check</Text>
              <Text style={styles.editModeDoneText}>{t('common.done')}</Text>
            </TouchableOpacity>
          </View>
        </>
      )}

      {/* Settings buttons on photo section */}
      {showSettings && !isEditMode && (
        <>
          {/* Display name label - Top Left */}
          {displayDeviceName && (
            <View style={styles.displayNameLabel}>
              <Text style={styles.displayNameIcon}>tv</Text>
              <Text style={styles.displayNameText}>{displayDeviceName}</Text>
            </View>
          )}
          <TouchableOpacity
            style={styles.settingsButton}
            onPress={onSettingsPress}
            activeOpacity={0.7}>
            <Text style={styles.settingsIcon}>settings</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.repositionButton}
            onPress={onEditPress}
            activeOpacity={0.7}>
            <Text style={styles.settingsIcon}>pan_tool</Text>
          </TouchableOpacity>
        </>
      )}

      {/* Loading progress (only when not showing settings) */}
      {!showSettings && loadingProgress && loadingProgress.loaded < loadingProgress.total && (
        <View style={styles.loadingProgressContainer}>
          <View style={styles.loadingProgressBar}>
            <View
              style={[
                styles.loadingProgressFill,
                {width: `${(loadingProgress.loaded / loadingProgress.total) * 100}%`},
              ]}
            />
          </View>
          <Text style={styles.loadingProgressText}>
            {loadingProgress.loaded}/{loadingProgress.total}
          </Text>
        </View>
      )}

      {/* First-time hint */}
      {showHint && (
        <View style={styles.hintOverlay}>
          <View style={styles.hintContainer}>
            <View style={styles.hintArrow}>
              <Text style={styles.hintArrowIcon}>north_east</Text>
            </View>
            <View style={styles.hintBubble}>
              <Text style={styles.hintIcon}>touch_app</Text>
              <View style={styles.hintContent}>
                <Text style={styles.hintText}>{t('frame.tapToReveal')}</Text>
                <Text style={styles.hintSubtext}>{t('frame.settingsButtonHere')}</Text>
              </View>
            </View>
            <TouchableOpacity
              style={styles.hintButton}
              onPress={onDismissHint}
              activeOpacity={0.8}>
              <Text style={styles.hintButtonText}>{t('common.understood')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );

  // Render the info section
  const renderInfoSection = () => (
    <View style={isPortrait ? styles.portraitInfoSection : styles.landscapeInfoSection}>
      {/* DateTime */}
      <View style={styles.dateTimeSection}>
        <Text style={[
          styles.greeting,
          isPortrait && styles.portraitGreeting,
          {fontSize: isPortrait ? scaledFonts.portraitGreeting : scaledFonts.greeting}
        ]}>
          {greeting}
        </Text>
        <Text style={[
          styles.time,
          isPortrait && styles.portraitTime,
          {fontSize: isPortrait ? scaledFonts.portraitTime : scaledFonts.time}
        ]}>
          {formattedTime}
        </Text>
        <Text style={[
          styles.date,
          isPortrait && styles.portraitDate,
          {fontSize: isPortrait ? scaledFonts.portraitDate : scaledFonts.date}
        ]}>
          {formattedDate.short}
        </Text>
      </View>

      {/* Reminders grouped by day */}
      <View style={styles.remindersSection}>
        <View style={styles.remindersHeader}>
          <Text style={styles.remindersIcon}>event</Text>
          <Text style={[styles.remindersTitle, {fontSize: scaledFonts.remindersTitle}]}>{t('frame.reminders')}</Text>
        </View>

        {reminderGroups.length > 0 ? (
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.remindersList}>
            {reminderGroups.map((group) => (
              <View key={group.date} style={styles.reminderGroup}>
                {/* Day label */}
                <View style={[
                  styles.dayLabelContainer,
                  group.isToday && styles.dayLabelContainerToday,
                ]}>
                  <Text style={[
                    styles.dayLabel,
                    group.isToday && styles.dayLabelToday,
                    {fontSize: scaledFonts.dayLabel},
                  ]}>
                    {group.label}
                  </Text>
                </View>

                {/* Reminders for this day */}
                {group.reminders.map((parsed) => (
                  <View
                    key={parsed.reminder.id}
                    style={[
                      styles.reminderItem,
                      group.isToday && styles.reminderItemToday,
                    ]}>
                    <Text style={[
                      styles.reminderTime,
                      group.isToday && styles.reminderTimeToday,
                      {fontSize: scaledFonts.reminderTime},
                    ]}>
                      {parsed.localTime}
                    </Text>
                    <Text
                      style={[
                        styles.reminderTitle,
                        group.isToday && styles.reminderTitleToday,
                        {fontSize: scaledFonts.reminderTitle},
                      ]}
                      numberOfLines={1}>
                      {parsed.reminder.title}
                    </Text>
                  </View>
                ))}
              </View>
            ))}
          </ScrollView>
        ) : (
          <Text style={styles.noReminders}>{t('frame.noReminders')}</Text>
        )}
      </View>
    </View>
  );

  // Portrait layout: photo top, info bottom
  if (isPortrait) {
    return (
      <View style={[styles.container, styles.portraitContainer]}>
        {renderPhotoSection()}
        {renderInfoSection()}
      </View>
    );
  }

  // Landscape layout: photo left, info right
  return (
    <View style={[styles.container, styles.landscapeContainer]}>
      {renderPhotoSection()}
      {renderInfoSection()}
    </View>
  );
}
